export default defineNuxtRouteMiddleware(async (to, from) => {
	// Skip during prerendering — there is no user session, and baking auth
	// redirects into static HTML causes redirect loops on the client.
	if (import.meta.prerender) {
		return;
	}

	if (to.path.startsWith('/api/')) {
		return;
	}

	// Parked "Coming soon" portals are fully public and have no login — the
	// only page is /coming-soon, so bounce every other route (including /login)
	// to it. fetchConfig() short-circuits when the config is already loaded, so
	// this is cheap for normal customers and leaves their auth flow unchanged.
	const { config, fetchConfig } = useCustomerConfig();
	if (!config.value) await fetchConfig();
	if (config.value?.comingSoon) {
		if (to.path !== '/coming-soon') {
			return navigateTo('/coming-soon', { redirectCode: 302 });
		}
		return;
	}

	const { state, verifyAuthentication, fetchChildAccounts } = useAuth();

	// Login page: allow access without triggering verification.
	// This avoids a redundant refresh call when the middleware redirects
	// an unauthenticated user here.
	if (to.path === '/login') {
		if (to.query.logout === 'true') return;
		if (state.value.isLoggedIn) {
			return navigateTo('/', { redirectCode: 302 });
		}
		return;
	}

	const authed = await verifyAuthentication();

	if (!authed) {
		const redirect = to.path === '/' ? '/login' : `/login?redirect=${to.path}`;
		return navigateTo(redirect, { redirectCode: 302 });
	}

	// Fetch child accounts during SSR so the initial render has account data,
	// preventing hydration mismatches in AuthAccountSelector / UiSelect.
	if (import.meta.server) {
		await fetchChildAccounts();
	}
});
